import { useId, useMemo, useState } from "react";

import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import { Button, Card, EmptyState, Loading, TextField } from "../../../ui";
import { STATE_WORDS, type DisplayState } from "../../../ui/state";
import { FILTER_STATES } from "../model/state";
import { buildNavModel, headingPath, hueIndexes, hueOf, type NavNode } from "../model/nav";
import { ItemTile } from "../parts/ItemTile";
import { ViewSwitch } from "../parts/ViewSwitch";

/** A gallery with more items than this starts with its groups folded. */
export const GALLERY_OPEN_LIMIT = 40;

interface Group {
  key: string;
  title: string;
  items: NavNode[];
}

/**
 * The gallery (DEC-1016-51): the optional second view, one tile for each item with a small map and a state ring.
 * With hundreds of items it folds by family or sub-family, and a search opens the groups that hold a match.
 */
export function GalleryPage() {
  const tree = useApiQuery("family_tree", {});
  const model = useMemo(() => buildNavModel(tree.data), [tree.data]);
  const hues = useMemo(() => hueIndexes(model), [model]);
  const searchId = useId();
  const stateId = useId();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<"all" | DisplayState>("all");
  const [override, setOverride] = useState<Map<string, boolean>>(new Map());

  const items = useMemo(() => model.nodes.filter((node) => node.kind === "item"), [model]);
  const needle = query.trim().toLowerCase();
  const groups = useMemo<Group[]>(() => {
    const byKey = new Map<string, Group>();
    for (const node of items) {
      const path = headingPath(model, node.id);
      if (state !== "all" && node.state !== state) continue;
      if (needle && !`${node.label} ${path}`.toLowerCase().includes(needle)) continue;
      const key = path || "";
      const group = byKey.get(key) ?? { key, title: path || "Not in a family yet", items: [] };
      group.items.push(node);
      byKey.set(key, group);
    }
    return Array.from(byKey.values())
      .map((group) => ({ ...group, items: group.items.sort((a, b) => a.label.localeCompare(b.label)) }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [items, model, needle, state]);

  const shown = groups.reduce((sum, group) => sum + group.items.length, 0);
  const startOpen = items.length <= GALLERY_OPEN_LIMIT || needle !== "";
  const isOpen = (key: string) => override.get(key) ?? startOpen;
  const setAll = (value: boolean) => setOverride(new Map(groups.map((group) => [group.key, value])));
  const toggle = (key: string) => setOverride((before) => new Map(before).set(key, !(before.get(key) ?? startOpen)));

  let body;
  if (tree.isPending) {
    body = <Loading>Reading the family tree</Loading>;
  } else if (tree.isError) {
    body = (
      <Card>
        <EmptyState title="The gallery could not be read" action={<Button onClick={() => void tree.refetch()}>Try again</Button>}>
          {refusalText(tree.error)}
        </EmptyState>
      </Card>
    );
  } else if (items.length === 0) {
    body = (
      <Card>
        <EmptyState title="No items yet">Each item your team adds gets a tile here, with a small map of its parts and a ring that shows how far it is.</EmptyState>
      </Card>
    );
  } else if (shown === 0) {
    body = (
      <Card>
        <EmptyState title="No item matches" action={<Button onClick={() => { setQuery(""); setState("all"); }}>Clear the search</Button>}>
          Try fewer letters, or another state.
        </EmptyState>
      </Card>
    );
  } else {
    body = (
      <div className="nv-gallery-groups">
        {groups.map((group) => {
          const open = isOpen(group.key);
          const panelId = `gallery-${group.key.replace(/\W+/g, "-") || "none"}`;
          return (
            <section key={group.key || "none"} className="nv-ggroup" aria-label={group.title}>
              <h2 className="nv-ggroup__title">
                <button type="button" aria-expanded={open} aria-controls={panelId} onClick={() => toggle(group.key)}>
                  <span aria-hidden="true">{open ? "▾" : "▸"}</span> {group.title} <span className="muted">({group.items.length})</span>
                </button>
              </h2>
              {open && (
                <ul id={panelId} className="nv-gallery">
                  {group.items.map((node) => (
                    <ItemTile key={node.id} node={node} progress={model.progress.get(node.id) ?? null} hue={hueOf(model, hues, node.id)} path={headingPath(model, node.id)} />
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div className="stack nv-gallery-page">
      <header className="nv-page__head">
        <h1>Gallery</h1>
        <ViewSwitch />
      </header>
      <p className="muted">One tile for each item, with a small map of its parts and a ring for how far it is. The family navigator is the main way to move around; the gallery is the second view.</p>
      {items.length > 0 && (
        <div className="nv-filters" role="group" aria-label="Search and filters">
          <TextField id={searchId} label="Find an item" type="search" value={query} onChange={(event) => setQuery(event.target.value)} autoComplete="off" />
          <div className="nv-filters__field">
            <label htmlFor={stateId}>Show only</label>
            <select id={stateId} value={state} onChange={(event) => setState(event.target.value as "all" | DisplayState)}>
              <option value="all">Every state</option>
              {FILTER_STATES.map((value) => (
                <option key={value} value={value}>
                  {STATE_WORDS[value]}
                </option>
              ))}
            </select>
          </div>
          <Button variant="quiet" onClick={() => setAll(true)}>
            Open all groups
          </Button>
          <Button variant="quiet" onClick={() => setAll(false)}>
            Fold all groups
          </Button>
          <p role="status" className="nv-filters__summary muted">
            {shown === 1 ? "1 item" : `${shown} items`}
          </p>
        </div>
      )}
      {body}
    </div>
  );
}
